import type { Action, Location, Update } from 'history';
import qs from 'qs';
import type { PropsWithChildren } from 'react';
import { useMemo, useCallback, useState, useTransition } from 'react';
import type { ResolveContext } from 'universal-router';
import { useIsomorphicLayoutEffect } from 'usehooks-ts';

import { updateMeta } from '@utils/dom-utils';

import type { RouteContextType } from './router.context';
import { LocationContext, RouteProvider } from './router.context';
import { useNavigator, useRouter } from './router.hooks';

const makeRouteResolveContext = ({
  pathname,
  search,
  hash,
}: Location): ResolveContext => ({
  pathname,
  query: qs.parse(search),
  hash,
});

const scrollPositionsHistory: Record<
  string,
  {
    scrollX: number;
    scrollY: number;
  }
> = {};

/**
 * This ia a top-level provider for a Universal Router instance. It manages the
 * current route context and listens for navigation events to update the current
 * location.
 */
export const Router = ({
  children,
  initialRouteContext,
}: PropsWithChildren<{
  initialRouteContext: RouteContextType;
}>) => {
  const [routeContext, setRouteContext] = useState(() => initialRouteContext);

  const router = useRouter();
  const history = useNavigator();

  const [isPending, startTransition] = useTransition();

  const [{ location, action }, setLocationContext] = useState(() => ({
    location: process.env.BROWSER ? history.location : ({} as Location),
    action: process.env.BROWSER ? history.action : ('' as Action),
  }));

  const context = useMemo(
    () => ({ location, action, isPending }),
    [location, action, isPending],
  );

  const resolveRoute = useCallback(
    async ({ pathname, query, hash }: ResolveContext) => {
      const newContext = await router.resolve({ pathname, query, hash });

      if (!newContext) return;

      setRouteContext(newContext);

      // Update document metadata
      // TODO: We can use React 19 builtins for this when we upgrade
      document.title = `${newContext.title} - Anchore`;
      updateMeta('description', newContext.description);
    },
    [router],
  );

  /**
   * Wrap navigation events in a transition so they are handled as deferred
   * state updates.
   */
  const handleLocationChange = useCallback(
    (update: Update) => {
      // Remember the latest scroll position for the previous location
      scrollPositionsHistory[update.location.key] = {
        scrollX: window.pageXOffset,
        scrollY: window.pageYOffset,
      };
      // Delete stored scroll position for next page if any
      if (action === 'PUSH') {
        delete scrollPositionsHistory[update.location.key];
      }

      startTransition(() => {
        setLocationContext(update);

        resolveRoute(makeRouteResolveContext(update.location));
      });
    },
    [resolveRoute],
  );

  /**
   * Start listening for navigation events on the client
   */
  useIsomorphicLayoutEffect(
    // A callback to stop listening is returned for cleanup
    () => history.listen(handleLocationChange),
    [history, handleLocationChange],
  );

  return (
    <LocationContext.Provider value={context}>
      <RouteProvider value={routeContext}>{children}</RouteProvider>
    </LocationContext.Provider>
  );
};

export default Router;
